import React, { useEffect, useState } from 'react';
import './App.css';
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  useLocation,
} from 'react-router-dom';
import {
  AppstoreOutlined,
  BankOutlined,
  ProjectOutlined,
  FileTextOutlined,
  CheckSquareOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Layout, Menu, Badge, Grid } from 'antd';
import AllClient from './components/clientPages/AllClient';
import Dashboard from './components/dashboard/Dashboard';
import Projects from './components/projectPages/Project';
import Nda from './components/clientPages/Nda';
import Msa from './components/clientPages/Msa';
import Sow from './components/projectPages/Sow';
import Cr from './components/projectPages/Cr';
import Po from './components/projectPages/Po';
import Notification from './components/headerItems/Notification';
import User from './components/headerItems/User';
import ClientDetail from './components/clientPages/ClientDetail';
import ContractDetailPage from './components/other/ContractDetailPage';
import PendingContracts from './components/contractPages/PendingContracts';
import ProjectsDetail from './components/other/ProjectsDetail';
import CommandPalette from './components/other/CommandPalette';
import ProtectedRoute from './ProtectedRoute';
import AppBreadcrumb from './components/other/AppBreadcrumb';
import GlobalLoader from './components/other/GlobalLoader';
import { contracts } from './components/api';
import { userRole } from './GetRole.utils';

const { Header, Sider, Content } = Layout;

const canApprove = userRole !== 'bdTeam';

const buildMenuItems = (pendingCount) => {
  const items = [
    {
      key: '/',
      icon: <AppstoreOutlined />,
      label: <Link to="/">Dashboard</Link>,
    },
    {
      key: '/clients',
      icon: <BankOutlined />,
      label: <Link to="/clients">Clients</Link>,
    },
    {
      key: '/projects',
      icon: <ProjectOutlined />,
      label: <Link to="/projects">Projects</Link>,
    },
    {
      key: 'contracts',
      icon: <FileTextOutlined />,
      label: 'Contracts',
      children: [
        { key: '/nda', label: <Link to="/nda" title="Non-Disclosure Agreement">NDA</Link> },
        { key: '/msa', label: <Link to="/msa" title="Master Service Agreement">MSA</Link> },
        { key: '/sow', label: <Link to="/sow" title="Statement of Work">SOW</Link> },
        { key: '/po', label: <Link to="/po" title="Purchase Order">PO</Link> },
        { key: '/cr', label: <Link to="/cr" title="Change Request">CR</Link> },
      ],
    },
  ];

  if (canApprove) {
    items.push({
      key: '/pendingApprovals',
      icon: <CheckSquareOutlined />,
      label: (
        <Link
          to="/pendingApprovals"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          Approvals
          {pendingCount > 0 && (
            <Badge
              count={pendingCount}
              style={{ background: '#FFFAEB', color: '#B54708', boxShadow: 'none', fontWeight: 600 }}
            />
          )}
        </Link>
      ),
    });
  }
  return items;
};

// "/clients/12" -> "/clients", "/contract/nda/5" -> "/nda"
const selectedMenuKey = (pathname) => {
  const parts = pathname.split('/');
  if (parts[1] === 'contract') return `/${parts[2]}`;
  if (parts.length > 2) return `/${parts[1]}`;
  return pathname || '/';
};

function App() {
  const location = useLocation();
  const screens = Grid.useBreakpoint();
  const [collapsed, setCollapsed] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const isDashboard = location.pathname === '/';
  const isMac = /Mac|iPhone|iPad/.test(navigator.platform);

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (!canApprove) return;
    contracts
      .pendingContractsCount()
      .then((res) => setPendingCount(Number(res.data.totalContractCount) || 0))
      .catch(() => setPendingCount(0));
  }, [location.pathname]);

  return (
    <Layout className="app-shell">
      <Sider
        width={240}
        className="app-sider"
        breakpoint="lg"
        collapsedWidth={screens.md ? 72 : 0}
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        trigger={null}
      >
        <div className="app-brand">
          <div className="app-brand-mark">S</div>
          {!collapsed && (
            <div className="app-brand-name">
              <strong>Spanidea</strong>
              <span>Contract Management</span>
            </div>
          )}
        </div>
        <Menu
          mode="inline"
          selectedKeys={[selectedMenuKey(location.pathname)]}
          defaultOpenKeys={['contracts']}
          items={buildMenuItems(pendingCount)}
        />
      </Sider>

      <Layout>
        <Header className="app-header">
          <button
            type="button"
            className="icon-btn"
            aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
            onClick={() => setCollapsed(!collapsed)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M3 6h18M3 12h18M3 18h18" />
            </svg>
          </button>
          <button
            type="button"
            className="app-search"
            onClick={() => setPaletteOpen(true)}
            aria-label="Search or jump to"
          >
            <SearchOutlined />
            <span className="grow">Search clients, projects, pages…</span>
            {screens.sm && <span className="kbd">{isMac ? '⌘ K' : 'Ctrl K'}</span>}
          </button>
          <div className="app-header-spacer" />
          <Notification />
          <User />
        </Header>

        <Content className="contentWrapper">
          <div className={isDashboard ? '' : 'page-surface'}>
            {!isDashboard && (
              <div className="breadcrumb-row">
                <AppBreadcrumb />
                <div id="breadcrumb-actions" className="breadcrumb-actions" />
              </div>
            )}
            <Routes>
              <Route
                path="/"
                element={<ProtectedRoute Component={Dashboard} />}
              />
              <Route path="/clients" element={<AllClient />} />
              <Route path="/clients/:id" element={<ClientDetail />} />
              <Route
                path="/contract/:contractType/:id"
                element={<ContractDetailPage />}
              />
              <Route path="/nda" element={<Nda />} />
              <Route path="/msa" element={<Msa />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/:id" element={<ProjectsDetail />} />
              <Route path="/sow" element={<Sow />} />
              <Route path="/cr" element={<Cr />} />
              <Route path="/po" element={<Po />} />

              <Route
                path="/pendingApprovals"
                element={<ProtectedRoute Component={PendingContracts} />}
              />
            </Routes>
          </div>
        </Content>
      </Layout>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <GlobalLoader />
    </Layout>
  );
}

function AppWrapper() {
  return (
    <BrowserRouter>
      <App />
    </BrowserRouter>
  );
}

export default AppWrapper;
